/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { ObservableMap } from '@typescript-kit/core';
import { ComponentData, ComponentModel, ComponentFieldKey } from '../model/component.model';
import { ViewComponentKey } from '../key/view-component.key';
import { TextModel } from './text.component-model';

export interface DropdownData extends ComponentData {
  trigger?: ComponentData | string;
  items?: Iterable<ComponentData>;
  scrollToActiveItem?: boolean;
}

export class DropdownFieldKey {
  static readonly TRIGGER = 'trigger';
  static readonly ITEMS = 'items';
  static readonly SCROLL_TO_ACTIVE_ITEM = 'scrollToActiveItem';
}

export class DropdownModel extends ComponentModel implements DropdownData {
  static readonly KEYS = ComponentModel.KEYS.concat(
    Object.keys(DropdownFieldKey).map((key) => DropdownFieldKey[key])
  );

  constructor(data?: DropdownData, keys?: string[]) {
    super(data, keys || DropdownModel.KEYS);
  }

  get trigger(): ComponentModel {
    return this.get(DropdownFieldKey.TRIGGER);
  }

  set trigger(value: ComponentModel) {
    this.set(DropdownFieldKey.TRIGGER, value);
  }

  get items(): ObservableMap<ComponentModel> {
    return this.get(DropdownFieldKey.ITEMS);
  }

  set items(value: ObservableMap<ComponentModel>) {
    this.set(DropdownFieldKey.ITEMS, value);
  }

  get scrollToActiveItem(): boolean {
    return this.get(DropdownFieldKey.SCROLL_TO_ACTIVE_ITEM);
  }

  set scrollToActiveItem(value: boolean) {
    this.set(DropdownFieldKey.SCROLL_TO_ACTIVE_ITEM, value);
  }

  protected createChildrenIterable(): Iterable<ComponentModel> {
    const self = this;
    return {
      [Symbol.iterator]: function* createChildrenGenerator() {
        if (self.trigger) {
          yield self.trigger;
        }
        for (const item of self.items) {
          yield item;
        }
      }
    };
  }

  protected setupValues(values: DropdownData, exclusive: boolean): DropdownData {
    if (exclusive && !(ComponentFieldKey.TYPE in values)) {
      values.type = ViewComponentKey.DROPDOWN;
    }
    if (typeof values.trigger === 'string') {
      values.trigger = new TextModel({ text: values.trigger });
    }
    this.setupChild(DropdownFieldKey.TRIGGER, values, exclusive);
    this.setupChildren(DropdownFieldKey.ITEMS, values, exclusive);
    return super.setupValues(values, exclusive) as DropdownData;
  }
}
